<script setup>
import { computed } from 'vue';
import { tv } from 'tailwind-variants';
import { darken, toHex } from 'color2k';
import { getContrastingTextColor } from '@chatwoot/utils';
import { CsSpinner } from 'dashboard/components/ui/commslayer';

const props = defineProps({
  bgColor: {
    type: String,
    required: true,
  },
  textColor: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'default',
    validator: value => ['sm', 'default', 'lg'].includes(value),
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

// Recipe using tailwind-variants with variant for state
const buttonRecipe = tv({
  slots: {
    button:
      'group relative cursor-pointer select-none inline-flex items-center gap-1 justify-center rounded-lg text-xs font-medium focus:outline-none',
    label: 'inline-flex items-center justify-center font-medium',
    loading:
      'absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center rounded-lg',
  },
  variants: {
    size: {
      sm: { button: 'h-6 px-2' },
      default: { button: 'h-7 px-3' },
      lg: { button: 'h-8 px-3' },
    },
    state: {
      normal: {
        button: [
          'bg-[var(--cxw-btn-bg)]',
          'hover:bg-[var(--cxw-btn-hover)]',
          'active:bg-[var(--cxw-btn-active)]',
          'text-[var(--cxw-btn-text)]',
          'focus-visible:outline-2',
          'focus-visible:outline-offset-2',
          'focus-visible:outline-[var(--cxw-btn-bg)]',
        ],
        label: 'group-active:translate-y-px',
      },
      disabled: {
        button:
          'bg-brand-black-alpha-9 dark:bg-brand-white-alpha-9 shadow-none text-white cursor-not-allowed',
      },
      loading: {
        button:
          'bg-brand-gray-6 dark:bg-brand-gray-14 shadow-none cursor-not-allowed',
        label: 'opacity-0',
        loading: 'text-brand-gray-10',
      },
    },
  },
  defaultVariants: {
    size: 'default',
    state: 'normal',
  },
});

// Determine current state
const currentState = computed(() => {
  if (props.isLoading) return 'loading';
  if (props.isDisabled) return 'disabled';
  return 'normal';
});

// Compute classes using recipe
const buttonClasses = computed(() =>
  buttonRecipe({
    size: props.size,
    state: currentState.value,
  })
);

// Only compute dynamic colors when in normal state
const isNormalState = computed(() => currentState.value === 'normal');

// Compute colors
const computedTextColor = computed(
  () => props.textColor || getContrastingTextColor(props.bgColor)
);

const hoverColor = computed(() => {
  try {
    return toHex(darken(props.bgColor, 0.08));
  } catch {
    return props.bgColor;
  }
});

const activeColor = computed(() => {
  try {
    return toHex(darken(props.bgColor, 0.15));
  } catch {
    return props.bgColor;
  }
});

// CSS variables only for normal state (disabled/loading use Tailwind classes)
const buttonStyle = computed(() => {
  if (!isNormalState.value) return {};

  return {
    '--cxw-btn-bg': props.bgColor,
    '--cxw-btn-hover': hoverColor.value,
    '--cxw-btn-active': activeColor.value,
    '--cxw-btn-text': computedTextColor.value,
  };
});
</script>

<template>
  <button
    data-component="CxwButton"
    :class="buttonClasses.button()"
    :style="buttonStyle"
    :disabled="isDisabled || isLoading"
    v-bind="$attrs"
    @click="e => emit('click', e)"
  >
    <span data-slot="label" :class="buttonClasses.label()">
      <slot />
    </span>
    <span v-if="isLoading" data-slot="loading" :class="buttonClasses.loading()">
      <CsSpinner size="xs" />
    </span>
  </button>
</template>
